import React, { useState } from 'react';
import { 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Plus, 
  Minus,
  Calculator,
  FileText,
  List,
  CheckCircle
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAssessmentStore } from '../../stores/assessmentStore';
import MathField from '../math/MathField';
import MathRenderer from '../math/MathRenderer';

const QuestionEditor = ({ question, questionNumber, assessmentId }) => {
  const { updateQuestion, deleteQuestion } = useAssessmentStore();
  const [isEditing, setIsEditing] = useState(!question.title);
  const [formData, setFormData] = useState({
    title: question.title || '',
    content: question.content || '',
    mathContent: question.mathContent || '',
    points: question.points || 1,
    options: question.options || [],
    correctAnswer: question.correctAnswer || '',
    explanation: question.explanation || ''
  });

  const handleSave = async () => {
    try {
      await updateQuestion(assessmentId, question.id, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: question.title || '',
      content: question.content || '',
      mathContent: question.mathContent || '',
      points: question.points || 1,
      options: question.options || [],
      correctAnswer: question.correctAnswer || '',
      explanation: question.explanation || ''
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await deleteQuestion(assessmentId, question.id);
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => i === index ? value : option)
    }));
  };

  const getQuestionIcon = (type) => {
    switch (type) {
      case 'math': return Calculator;
      case 'multiple-choice': return List;
      case 'true-false': return CheckCircle;
      default: return FileText;
    }
  };

  const QuestionIcon = getQuestionIcon(question.type);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Question Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-8 h-8 bg-sigma-100 rounded-full">
            <span className="text-sm font-medium text-sigma-700">{questionNumber}</span>
          </div>
          <div className="flex items-center space-x-2">
            <QuestionIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-500 capitalize">
              {question.type.replace('-', ' ')} Question
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-gray-500 hover:text-sigma-600 rounded-lg hover:bg-gray-100"
                title="Edit question"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-red-500 hover:text-red-600 rounded-lg hover:bg-red-50"
                title="Delete question"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="p-2 text-green-600 hover:text-green-700 rounded-lg hover:bg-green-50"
                title="Save changes"
              >
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancel}
                className="p-2 text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Cancel editing"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Question Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              placeholder="Enter question title..."
            />
          </div>

          {/* Question Content */}
          {question.type !== 'math' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Content
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                placeholder="Enter question content..."
              />
            </div>
          )}

          {/* Math Content */}
          {question.type === 'math' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mathematical Expression
              </label>
              <MathField
                value={formData.mathContent}
                onChange={(latex) => setFormData(prev => ({ ...prev, mathContent: latex }))}
                placeholder="Enter mathematical expression..."
                className="w-full"
              />
              {formData.mathContent && (
                <div className="mt-2 p-3 bg-gray-50 rounded border">
                  <div className="text-xs text-gray-500 mb-2">Preview:</div>
                  <MathRenderer latex={formData.mathContent} />
                </div>
              )}
            </div>
          )}

          {/* Multiple Choice Options */}
          {question.type === 'multiple-choice' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options
              </label>
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500 w-6">
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                    />
                    {formData.options.length > 2 && (
                      <button
                        onClick={() => removeOption(index)}
                        className="p-2 text-red-500 hover:text-red-600"
                        title="Remove option"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {formData.options.length < 6 && (
                  <button
                    onClick={addOption}
                    className="flex items-center text-sm text-sigma-600 hover:text-sigma-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Option
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Correct Answer */}
          {(question.type === 'multiple-choice' || question.type === 'true-false') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Answer
              </label>
              {question.type === 'multiple-choice' ? (
                <select
                  value={formData.correctAnswer}
                  onChange={(e) => setFormData(prev => ({ ...prev, correctAnswer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                >
                  <option value="">Select correct answer</option>
                  {formData.options.map((option, index) => (
                    <option key={index} value={index}>
                      {String.fromCharCode(65 + index)}. {option}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={formData.correctAnswer}
                  onChange={(e) => setFormData(prev => ({ ...prev, correctAnswer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                >
                  <option value="">Select correct answer</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              )}
            </div>
          )}

          {/* Points */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={formData.points}
                onChange={(e) => setFormData(prev => ({ ...prev, points: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              />
            </div>
          </div>

          {/* Explanation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Explanation (Optional)
            </label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              placeholder="Provide an explanation for the correct answer..."
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Display Question */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">{question.title}</h4>
            
            {question.type === 'math' && question.mathContent ? (
              <div className="p-4 bg-gray-50 rounded-lg">
                <MathRenderer latex={question.mathContent} />
              </div>
            ) : question.content ? (
              <p className="text-gray-700">{question.content}</p>
            ) : (
              <p className="text-gray-400 italic">No content provided</p>
            )}
          </div>

          {/* Display Options */}
          {question.type === 'multiple-choice' && question.options && (
            <div className="space-y-2">
              {question.options.map((option, index) => (
                <div
                  key={index}
                  className={clsx(
                    'flex items-center p-2 rounded border',
                    question.correctAnswer == index
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-gray-50 border-gray-200'
                  )}
                >
                  <span className="font-medium mr-2">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span>{option}</span>
                  {question.correctAnswer == index && (
                    <span className="ml-auto text-green-600 text-xs font-medium">
                      Correct
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {question.type === 'true-false' && (
            <div className="space-y-2">
              {['true', 'false'].map((option) => (
                <div
                  key={option}
                  className={clsx(
                    'flex items-center p-2 rounded border',
                    question.correctAnswer === option
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-gray-50 border-gray-200'
                  )}
                >
                  <span className="capitalize">{option}</span>
                  {question.correctAnswer === option && (
                    <span className="ml-auto text-green-600 text-xs font-medium">
                      Correct
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Question Info */}
          <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-gray-200">
            <span>Points: {question.points}</span>
            <span className="capitalize">{question.type.replace('-', ' ')}</span>
          </div>

          {/* Explanation */}
          {question.explanation && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="text-xs font-medium text-blue-800 mb-1">Explanation:</div>
              <div className="text-sm text-blue-700">{question.explanation}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionEditor;